
import React, { useEffect, useState } from "react";
import { Footer } from "../component/footersection";
import { useNavigate } from "react-router-dom";

const BASE_URL = "https://api.ajpartyhouse.in";

export default function RoomsPage() {
  const navigate = useNavigate();

  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const fetchRooms = async () => {
      console.log("🔥 ROOMS PAGE LOADED");
      console.log("🔥 API:", `${BASE_URL}/rooms/all`);

      try {
        setLoading(true);
        setError("");

        const controller = new AbortController();

        const timeoutId = setTimeout(() => {
          console.log("❌ Rooms API timeout");
          controller.abort();
        }, 15000);

        const res = await fetch(`${BASE_URL}/rooms/all`, {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        console.log("✅ Rooms API status:", res.status);

        if (!res.ok) {
          throw new Error(`API returned status ${res.status}`);
        }

        const data = await res.json();

        console.log("✅ Rooms API response:", data);
        console.log("✅ Is response array:", Array.isArray(data));

        if (!isMounted) return;

        /*
          API currently returns:

          [
            {
              id: 4,
              name: "...",
              capacity: 200,
              price: "25000.00",
              description: "...",
              image_url: "..."
            }
          ]

          But we also support:
          { rooms: [...] }
          { data: [...] }
        */

        let roomsData = [];

        if (Array.isArray(data)) {
          roomsData = data;
        } else if (Array.isArray(data?.rooms)) {
          roomsData = data.rooms;
        } else if (Array.isArray(data?.data)) {
          roomsData = data.data;
        } else {
          console.error("❌ Invalid rooms response:", data);

          setError("Invalid rooms data received from server.");
          setRooms([]);
          return;
        }

        console.log("✅ FINAL ROOMS:", roomsData);
        console.log(
          "✅ FINAL ROOMS IS ARRAY:",
          Array.isArray(roomsData)
        );

        // IMPORTANT:
        // Always make sure state receives an array.
        setRooms(Array.isArray(roomsData) ? roomsData : []);
      } catch (err) {
        console.error("❌ Rooms fetch error:", err);

        if (!isMounted) return;

        if (err?.name === "AbortError") {
          setError(
            "The rooms server took too long to respond. Please try again."
          );
        } else {
          setError(
            err?.message || "Unable to load rooms. Please try again."
          );
        }

        setRooms([]);
      } finally {
        if (isMounted) {
          console.log("🔥 Rooms loading finished");
          setLoading(false);
        }
      }
    };

    fetchRooms();

    return () => {
      isMounted = false;
    };
  }, []);

  /*
    LOADING
  */
  if (loading) {
    return (
      <>
        <section className="rooms-loading">
          <div className="loading-box">
            <div className="spinner"></div>
            <h2>Loading rooms...</h2>
            <p>Please wait while we load our premium rooms.</p>
          </div>
        </section>

        <Footer />

        <style>{`
          .rooms-loading {
            min-height: 65vh;
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 80px 20px;
            background: #fff8f2;
            text-align: center;
          }

          .loading-box {
            display: flex;
            flex-direction: column;
            align-items: center;
          }

          .loading-box h2 {
            margin: 20px 0 8px;
            color: #222;
            font-size: 28px;
          }

          .loading-box p {
            color: #777;
            margin: 0;
          }

          .spinner {
            width: 45px;
            height: 45px;
            border: 4px solid #eee;
            border-top: 4px solid #c59d5f;
            border-radius: 50%;
            animation: roomsSpin 1s linear infinite;
          }

          @keyframes roomsSpin {
            from {
              transform: rotate(0deg);
            }

            to {
              transform: rotate(360deg);
            }
          }
        `}</style>
      </>
    );
  }

  /*
    ERROR
  */
  if (error) {
    return (
      <>
        <section className="rooms-error">
          <div className="error-box">
            <div className="error-icon">!</div>

            <h2>Unable to Load Rooms</h2>

            <p>{error}</p>

            <button
              type="button"
              onClick={() => window.location.reload()}
            >
              Try Again
            </button>
          </div>
        </section>

        <Footer />

        <style>{`
          .rooms-error {
            min-height: 65vh;
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 80px 20px;
            background: #fff8f2;
            text-align: center;
          }

          .error-box {
            max-width: 500px;
          }

          .error-icon {
            width: 55px;
            height: 55px;
            margin: 0 auto 20px;
            border-radius: 50%;
            background: #c59d5f;
            color: white;
            font-size: 35px;
            font-weight: bold;
            display: flex;
            justify-content: center;
            align-items: center;
          }

          .error-box h2 {
            color: #222;
            margin-bottom: 10px;
          }

          .error-box p {
            color: #777;
            line-height: 1.6;
          }

          .error-box button {
            margin-top: 15px;
            background: #c59d5f;
            color: white;
            border: none;
            padding: 12px 28px;
            border-radius: 30px;
            cursor: pointer;
            font-size: 16px;
            font-weight: 600;
          }

          .error-box button:hover {
            background: #a87a3b;
          }
        `}</style>
      </>
    );
  }

  /*
    SAFETY CHECK

    Even if something unexpected happens,
    never call .map() on a non-array.
  */
  const safeRooms = Array.isArray(rooms) ? rooms : [];

  return (
    <>
      <section id="rooms" className="rooms-section">
        <h2>Our Premium Party Rooms</h2>

        {safeRooms.length === 0 ? (
          <div className="no-rooms">
            <h3>No rooms available</h3>
            <p>
              We currently don't have any rooms available.
            </p>
          </div>
        ) : (
          <div className="rooms-container">
            {safeRooms.map((room, index) => {
              /*
                SAFE ROOM VALUES
              */

              const roomId =
                room?.id !== null &&
                room?.id !== undefined
                  ? room.id
                  : index;

              const roomName =
                typeof room?.name === "string" &&
                room.name.trim() !== ""
                  ? room.name.trim()
                  : "Party Room";

              const roomDescription =
                typeof room?.description === "string" &&
                room.description.trim() !== ""
                  ? room.description
                      .replace(/"/g, "")
                      .trim()
                  : "A beautiful party room for your special celebrations.";

              const roomCapacity =
                room?.capacity !== null &&
                room?.capacity !== undefined
                  ? room.capacity
                  : "N/A";

              const roomPrice =
                room?.price !== null &&
                room?.price !== undefined
                  ? room.price
                  : null;

              const imageUrl =
                typeof room?.image_url === "string" &&
                room.image_url.trim() !== ""
                  ? room.image_url.trim()
                  : null;

              return (
                <div
                  key={roomId}
                  className="room-card"
                >
                  <div className="room-image-wrapper">
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={roomName}
                        className="room-image"
                        loading="lazy"
                        onError={(e) => {
                          console.error(
                            "❌ Image failed:",
                            imageUrl
                          );

                          e.currentTarget.style.display =
                            "none";

                          const parent =
                            e.currentTarget.parentElement;

                          if (parent) {
                            parent.classList.add(
                              "image-error"
                            );
                          }
                        }}
                      />
                    ) : (
                      <div className="image-placeholder">
                        <span>Party Room</span>
                      </div>
                    )}
                  </div>

                  <div className="room-content">
                    <h3>{roomName}</h3>

                    <p className="capacity">
                      <strong>Capacity:</strong>{" "}
                      {roomCapacity} Guests
                    </p>

                    {roomPrice && (
                      <p className="price">
                        ₹
                        {Number(roomPrice).toLocaleString(
                          "en-IN"
                        )}
                      </p>
                    )}

                    <p className="description">
                      {roomDescription}
                    </p>

                    <button
                      type="button"
                      onClick={() =>
                        navigate(
                          `/bookinghall/${roomId}`
                        )
                      }
                    >
                      Book Now
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <Footer />

      <style>{`
        .rooms-section {
          width: 100%;
          box-sizing: border-box;
          padding: 120px 60px;
          text-align: center;
          background: #fff8f2;
          min-height: 60vh;
        }

        .rooms-section h2 {
          font-size: 3rem;
          font-weight: 700;
          margin: 0 0 60px;
          color: #222;
        }

        .rooms-container {
          width: 100%;
          max-width: 1200px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 40px;
        }

        .room-card {
          width: 100%;
          box-sizing: border-box;
          background: #fff;
          border-radius: 25px;
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.1);
          transition: transform 0.4s ease,
                      box-shadow 0.4s ease;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .room-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
        }

        .room-image-wrapper {
          width: 100%;
          height: 280px;
          overflow: hidden;
          background: #f1f1f1;
        }

        .room-image {
          width: 100%;
          height: 100%;
          display: block;
          object-fit: cover;
          transition: transform 0.4s ease;
        }

        .room-card:hover .room-image {
          transform: scale(1.04);
        }

        .image-placeholder,
        .image-error {
          width: 100%;
          height: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
          background: #eee;
          color: #777;
          font-size: 18px;
        }

        .room-content {
          padding: 30px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
        }

        .room-content h3 {
          font-size: 1.75rem;
          color: #222;
          margin: 0;
        }

        .room-content p {
          color: #555;
          font-size: 16px;
          line-height: 1.6;
          margin: 0;
        }

        .room-content .capacity {
          color: #444;
        }

        .room-content .price {
          color: #c59d5f;
          font-size: 22px;
          font-weight: 700;
        }

        .room-content .description {
          max-width: 600px;
        }

        .room-content button {
          margin-top: 12px;
          background: #c59d5f;
          padding: 12px 30px;
          border-radius: 50px;
          border: none;
          color: #fff;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .room-content button:hover {
          background: #a87a3b;
          transform: scale(1.05);
        }

        .no-rooms {
          padding: 50px 20px;
          color: #777;
        }

        .no-rooms h3 {
          color: #333;
          font-size: 24px;
          margin-bottom: 10px;
        }

        .no-rooms p {
          margin: 0;
        }

        /*
          TABLET
        */

        @media (max-width: 1024px) {
          .rooms-section {
            padding: 100px 40px;
          }

          .rooms-container {
            grid-template-columns: repeat(
              2,
              minmax(0, 1fr)
            );
            gap: 30px;
          }

          .rooms-section h2 {
            font-size: 2.5rem;
          }

          .room-image-wrapper {
            height: 240px;
          }

          .room-content {
            padding: 25px;
          }
        }

        /*
          MOBILE
        */

        @media (max-width: 768px) {
          .rooms-section {
            padding: 80px 20px;
          }

          .rooms-section h2 {
            font-size: 2rem;
            margin-bottom: 40px;
          }

          .rooms-container {
            grid-template-columns: 1fr;
            gap: 25px;
          }

          .room-card {
            border-radius: 18px;
          }

          .room-image-wrapper {
            height: 220px;
          }

          .room-content {
            padding: 22px;
          }

          .room-content h3 {
            font-size: 1.4rem;
          }

          .room-content p {
            font-size: 14px;
          }

          .room-content .price {
            font-size: 20px;
          }

          .room-content button {
            font-size: 14px;
            padding: 10px 22px;
          }
        }

        /*
          SMALL MOBILE
        */

        @media (max-width: 480px) {
          .rooms-section {
            padding: 70px 15px;
          }

          .rooms-section h2 {
            font-size: 1.8rem;
          }

          .room-card {
            border-radius: 16px;
          }

          .room-image-wrapper {
            height: 180px;
          }

          .room-content {
            padding: 18px;
          }

          .room-content h3 {
            font-size: 1.2rem;
          }

          .room-content p {
            font-size: 13px;
          }

          .room-content button {
            font-size: 13px;
            padding: 8px 20px;
          }
        }
      `}</style>
    </>
  );
}
